import { api } from "@/comn";
import { comnUtils } from "@/comn/utils";
import { colEnvs, colUtils } from "@/tz/go/egaz/osci/colt/comn"; // 시스템 공통 유틸
import { TFormSchema, TGridSchema } from "@/comn/hooks";
import { lpcoEnvs } from "@/tz/go/egaz/osci/lpco/comn";

/*
 * @ BASE
 * @ 서비스에서 사용하는 기본 정보 세팅
 * @ path : 화면들의 기본 웹 경로
 * @ api : 사용하는 api 의 기본 주소
 * @ api 주소가 여러개일 경우 _ 로구분하여 의미에 맞게 카멜표기업으로 표시 : api_clr
 * @ nodes : 화면의 Page.Navigation 에서 사용하는 기본 결로 Array
 */
export const BASE = {
    path: `${lpcoEnvs.base}/colt/intf`,
    api: `${process.env.REACT_APP_API_OSCI}/api/v1/osci/colt/intf/ntfc/zmp`,
    nodes: [
        { path: "/", label: "Collection" },
        { path: "/colt/", label: "Collection Management" },
        { path: "/colt/intf/", label: "Notice Interface Management" },
    ],
};

/*
 * @ URLS
 * @ 화면에서 사용하는 주소들 정의
 * @ 해당화면의 컴포넌트명 대문자로 정의
 */
export const URLS = {
    osci0406001q: `/osci/colt/intf/osci0406001q`, // 고지 송신 내역 목록 조회
    osci0406002q: `/osci/colt/intf/osci0406002q`, // 고지 수신 내역 목록 조회
    coli0601003q: `/osci/colt/intf/coli0601003q`, // 담보 수신 내역 목록 조회
    osci0406003q: `/osci/colt/intf/osci0406003q`, // 고지 재전송 목록 조회
    osci0406004q: `/osci/colt/intf/osci0406004q`, // 고지 재전송 실패 목록 조회
    osci0401002q: `/osci/colt/ntfc/osci0401002q`, // 고지정보 조회 팝업
    osci0202002q: `${lpcoEnvs.base}/lpco/tansad/osci0202002q`, // TANSAD정보 조회 팝업
};

/*
 * @ APIS
 * @ 화면에서 사용하는 api 정의
 */
export const APIS = {
    // Get Bill Send List !== 고지 송신 목록 조회 ==!
    getBllSendList: (data: any, page: number, size: number) => {
        return api.get(`${BASE.api}/send?page=${page}&size=${size}`, {
            params: comnUtils.toGetParams(data),
        });
    },
    // Get Bill Reception List !== 고지 수신 목록 조회 ==!
    getBllRcpnList: (data: any, page: number, size: number) => {
        return api.get(`${BASE.api}/rcpn?page=${page}&size=${size}`, {
            params: comnUtils.toGetParams(data),
        });
    },
    // Get Security Reception List !== 담보 수신 목록 조회 ==!
    getSecuRcpnList: (data: any, page: number, size: number) => {
        return api.get(`${BASE.api}/secu?page=${page}&size=${size}`, {
            params: comnUtils.toGetParams(data),
        });
    },
    // Bill Re-send List !== 고지 재전송 목록 조회 ==!
    getBllResdList: (data: any, page: number, size: number) => {
        return api.get(`${BASE.api}/resend?page=${page}&size=${size}`, {
            params: comnUtils.toGetParams(data),
        });
    },
    // Get Bill Re-send Fail List !== 고지 재전송 실패 목록 조회 ==!
    getBillResdFailList: (data: any, page: number, size: number) => {
        return api.get(`${BASE.api}/fail?page=${page}&size=${size}`, {
            params: comnUtils.toGetParams(data),
        });
    },
    // ZanMalipo Send !== ZanMalipo 전송 ==!
    zmpSend: (data: any) => {
        return api.post(`${BASE.api}/sendZanMalipo`, {
            ...data,
        });
    },
};

/*
 * @ 그리드 스키마 정의
 * @ 화면에서 사용하는 그리드 구조 정의
 * @ SG_ 로 시작 (Schema grid)
 * @ SG_[그리드명과 조회하는 데이터 이름 메타 단어(용어) 대문자, "_" 로 구분 , 그리드명 맨 뒤에 "_LIST" 로 종료]
 */
//============================================================================================================================
// 목록
//============================================================================================================================
// Schema of Bill Send List Grid !== 고지 송신 목록 그리드 스키마 ==!
export const SG_BLL_SEND_LIST: TGridSchema = {
    id: "grid_bllSendList",
    options: {
        pagination: "out", // 그리드 데이터 페이징 "out" : 데이터를 외부에서 페이징해서 가져오는 경우 , "in" : 데이터를 전체 가져와서 그리드 내부에서 페이징 및 편집
        index: true, // 그리드 번호  index : true ( asc )  , index : "DESC" ( desc 역순 )
        edit: false, // 그리드 편집 가능 여부 , default : false
    },
    head: [
        { cells: [{ header: "L_BLL_NO", binding: "bllNo", width: 170 }] },
        { cells: [{ header: "L_TANSAD_NO", binding: "tansadNo", width: 150 }] },
        { cells: [{ header: "L_DGR", binding: "sendDgr", width: 70 }] },
        { cells: [{ header: "L_SEND_DT", binding: "sendDt", width: 100 }] },
        { cells: [{ header: "L_NTFC_TP", binding: "ntcTpNm", width: 100 }] },
        { cells: [{ header: "L_TXPR_TIN", binding: "txprTin", width: "*" }] },
        { cells: [{ header: "L_TOT_AMT", binding: "totAmt", width: 140 }] },
        { cells: [{ header: "L_SEND_STTS", binding: "stts", width: 100 }] },
    ],
    body: [
        { cells: [{ binding: "bllNo" }] },
        { cells: [{ binding: "tansadNo" }] },
        { cells: [{ binding: "sendDgr" }] },
        { cells: [{ binding: "sendDt", type: "date" }] },
        { cells: [{ binding: "ntcTpNm" }] },
        { cells: [{ binding: "txprTin", align: "left" }] },
        { cells: [{ binding: "totAmt", type: "number", thousandSeparator: true, align: "right" }] },
        { cells: [{ binding: "stts" }] },
    ],
};

// Schema of Bill Reception List Grid !== 고지 수신 목록 그리드 스키마 ==!
export const SG_BLL_RCPN_LIST: TGridSchema = {
    id: "grid_bllRcpnList",
    options: {
        pagination: "out", // 그리드 데이터 페이징 "out" : 데이터를 외부에서 페이징해서 가져오는 경우 , "in" : 데이터를 전체 가져와서 그리드 내부에서 페이징 및 편집
        index: true, // 그리드 번호  index : true ( asc )  , index : "DESC" ( desc 역순 )
        edit: false, // 그리드 편집 가능 여부 , default : false
    },
    head: [
        { cells: [{ header: "L_BLL_NO", binding: "bllNo", width: 160 }] },
        { cells: [{ header: "L_TANSAD_NO", binding: "tansadNo", width: 140 }] },
        { cells: [{ header: "L_RCPN_DT", binding: "rcveDt", width: 100 }] },
        { cells: [{ header: "L_DGR", binding: "zmpRcveSrno", width: 70 }] },
        { cells: [{ header: "L_TXPR_TIN", binding: "txprTin", width: "*" }] },
        { cells: [{ header: "L_RCPT_NO", binding: "rcptNo", width: 140 }] },
        { cells: [{ header: "L_RCPT_DT", binding: "rcptDtm", width: 100 }] },
        { cells: [{ header: "L_RCPT_AMT", binding: "rcptAmt", width: 120 }] },
        { cells: [{ header: "L_RCVE_STTS", binding: "stts", width: 100 }] },
    ],
    body: [
        { cells: [{ binding: "bllNo" }] },
        { cells: [{ binding: "tansadNo" }] },
        { cells: [{ binding: "rcveDt", type: "date" }] },
        { cells: [{ binding: "zmpRcveSrno" }] },
        { cells: [{ binding: "txprTin", align: "left" }] },
        { cells: [{ binding: "rcptNo" }] },
        { cells: [{ binding: "rcptDtm", type: "date" }] },
        { cells: [{ binding: "rcptAmt", type: "number", thousandSeparator: true, align: "right" }] },
        { cells: [{ binding: "stts" }] },
    ],
};

// Schema of Security Reception List Grid !== 담보 수신 목록 그리드 스키마 ==!
export const SG_SECU_RCPN_LIST: TGridSchema = {
    id: "grid_secuRcpnList",
    options: {
        pagination: "out", // 그리드 데이터 페이징 "out" : 데이터를 외부에서 페이징해서 가져오는 경우 , "in" : 데이터를 전체 가져와서 그리드 내부에서 페이징 및 편집
        index: true, // 그리드 번호  index : true ( asc )  , index : "DESC" ( desc 역순 )
        edit: false, // 그리드 편집 가능 여부 , default : false
    },
    head: [
        { cells: [{ header: "L_TXPR_TIN", binding: "txprTin", width: 230 }] },
        { cells: [{ header: "L_RCPN_DT", binding: "rcveDt", width: 150 }] },
        { cells: [{ header: "L_DPST_TP2", binding: "coltDpstTpCd", width: 100 }] },
        { cells: [{ header: "L_PAY_DT", binding: "payDtm", width: 120 }] },
        { cells: [{ header: "L_PAY_MODE", binding: "payModeCd", width: 130 }] },
        { cells: [{ header: "L_PAY_AMT", binding: "payAmt", width: 130 }] },
        { cells: [{ header: "L_PAY_BNK", binding: "payBnkCd", width: 200 }] },
        { cells: [{ header: "L_PAY_BNK_ACCT_NO", binding: "payBnkAcctNo", width: 180 }] },
        { cells: [{ header: "L_RCPT_BNK_REFF_NO", binding: "bnkReffNo", width: 150 }] },
        { cells: [{ header: "L_RCVE_STTS", binding: "stts", width: "*" }] },
    ],
    body: [
        { cells: [{ binding: "txprTin", type: "code", area: "coCd" }] },
        { cells: [{ binding: "rcveDt", type: "text" }] },
        { cells: [{ binding: "coltDpstTpCd" }] },
        { cells: [{ binding: "payDtm", type: "date" }] },
        { cells: [{ binding: "payModeCd", type: "code", area: "comnCd", comnCd: "OSC0073" }] },
        { cells: [{ binding: "payAmt", type: "number", thousandSeparator: true }] },
        { cells: [{ binding: "payBnkCd", type: "code", area: "bnkCd" }] },
        { cells: [{ binding: "payBnkAcctNo" }] },
        { cells: [{ binding: "bnkReffNo" }] },
        { cells: [{ binding: "stts" }] },
    ],
};

// Schema of Bill Re-send List Grid !== 고지 재전송 목록 그리드 스키마 ==!
export const SG_BLL_RESD_LIST: TGridSchema = {
    id: "grid_bllResdList",
    options: {
        pagination: "out", // 그리드 데이터 페이징 "out" : 데이터를 외부에서 페이징해서 가져오는 경우 , "in" : 데이터를 전체 가져와서 그리드 내부에서 페이징 및 편집
        index: true, // 그리드 번호  index : true ( asc )  , index : "DESC" ( desc 역순 )
        edit: false, // 그리드 편집 가능 여부 , default : false
    },
    head: [
        { cells: [{ header: "L_BLL_NO", binding: "bllNo", width: 170 }] },
        { cells: [{ header: "L_TANSAD_NO", binding: "tansadNo", width: 150 }] },
        { cells: [{ header: "L_DGR", binding: "sendDgr", width: 70 }] },
        { cells: [{ header: "L_SEND_DT", binding: "sendDt", width: 100 }] },
        { cells: [{ header: "L_NTFC_TP", binding: "ntcTpNm", width: 100 }] },
        { cells: [{ header: "L_TXPR_TIN", binding: "txprTin", width: "*" }] },
        { cells: [{ header: "L_TOT_AMT", binding: "totAmt", width: 150 }] },
        { cells: [{ header: "L_SEND_STTS", binding: "stts", width: 100 }] },
        { cells: [{ header: "L_ZMP_SEND", binding: "zmpSend", width: 150 }] },
    ],
    body: [
        { cells: [{ binding: "bllNo" }] },
        { cells: [{ binding: "tansadNo" }] },
        { cells: [{ binding: "sendDgr" }] },
        { cells: [{ binding: "sendDt", type: "date" }] },
        { cells: [{ binding: "ntcTpNm" }] },
        { cells: [{ binding: "txprTin", align: "left" }] },
        { cells: [{ binding: "totAmt", type: "number", thousandSeparator: true, align: "right" }] },
        { cells: [{ binding: "stts", type: "code", area: "comnCd", comnCd: "OSC0075" }] },
        { cells: [{ binding: "zmpSend" }] },
    ],
};

// Schema of Bill Re-send Fail List Grid !== 고지 재전송 실패 목록 그리드 스키마 ==!
export const SG_BLL_RESD_FAIL_LIST: TGridSchema = {
    id: "grid_bllResdFailList",
    options: {
        // checkbox: true,
        pagination: "out", // 그리드 데이터 페이징 "out" : 데이터를 외부에서 페이징해서 가져오는 경우 , "in" : 데이터를 전체 가져와서 그리드 내부에서 페이징 및 편집
        index: true, // 그리드 번호  index : true ( asc )  , index : "DESC" ( desc 역순 )
        edit: false, // 그리드 편집 가능 여부 , default : false
    },
    head: [
        { cells: [{ header: "L_BLL_NO", binding: "bllNo", width: 170 }] },
        { cells: [{ header: "L_TANSAD_NO", binding: "tansadNo", width: 150 }] },
        { cells: [{ header: "L_DGR", binding: "sendDgr", width: 70 }] },
        { cells: [{ header: "L_SEND_DT", binding: "sendDt", width: 100 }] },
        { cells: [{ header: "L_NTFC_TP", binding: "ntcTpNm", width: 100 }] },
        { cells: [{ header: "L_TXPR_TIN", binding: "txprTin", width: "2*" }] },
        { cells: [{ header: "L_TOT_AMT", binding: "totAmt", width: 140 }] },
        { cells: [{ header: "L_SEND_STTS", binding: "stts", width: "*" }] },
    ],
    body: [
        { cells: [{ binding: "bllNo" }] },
        { cells: [{ binding: "tansadNo" }] },
        { cells: [{ binding: "sendDgr" }] },
        { cells: [{ binding: "sendDt", type: "date" }] },
        { cells: [{ binding: "ntcTpNm" }] },
        { cells: [{ binding: "txprTin", align: "left" }] },
        { cells: [{ binding: "totAmt", type: "number", thousandSeparator: true, align: "right" }] },
        { cells: [{ binding: "stts", type: "code", area: "comnCd", comnCd: "OSC0075" }] },
    ],
};

// Schema of FTCS Re-send Fail List Grid !== FTCS 재전송 실패 목록 그리드 스키마 ==!
export const SG_FTCS_RESD_FAIL_LIST: TGridSchema = {
    id: "grid_ftcsResdFailList",
    options: {
        checkbox: true,
        pagination: "out", // 그리드 데이터 페이징 "out" : 데이터를 외부에서 페이징해서 가져오는 경우 , "in" : 데이터를 전체 가져와서 그리드 내부에서 페이징 및 편집
        index: true, // 그리드 번호  index : true ( asc )  , index : "DESC" ( desc 역순 )
        edit: false, // 그리드 편집 가능 여부 , default : false
    },
    head: [
        { cells: [{ header: "L_FTCS_SEND_NO", binding: "ftcsSendNo", width: 180 }] },
        { cells: [{ header: "L_FTCS_SEND_TP", binding: "ftcsSendTpCd", width: 350 }] },
        { cells: [{ header: "L_REFF_NO", binding: "sendReffNo", width: 180 }] },
        { cells: [{ header: "L_STTS", binding: "reffStatCdNm", width: "*" }] },
        { cells: [{ header: "L_SEND_DTM", binding: "sendDtm", width: 180 }] },
        { cells: [{ header: "L_SEND_STTS", binding: "stts", width: 120 }] },
    ],
    body: [
        { cells: [{ binding: "ftcsSendNo", mask: colUtils.getMaskPattern("8,7,2", "-") }] },
        { cells: [{ binding: "ftcsSendTpCd", type: "code", area: "comnCd", comnCd: "OSC0076" }] },
        { cells: [{ binding: "sendReffNo" }] },
        { cells: [{ binding: "reffStatCdNm" }] },
        { cells: [{ binding: "sendDtm", type: "datetime" }] },
        { cells: [{ binding: "stts", type: "code", area: "comnCd", comnCd: "OSC0075" }] },
    ],
};

/*
 * @ Form 스키마 정의
 * @ 화면에서 사용하는 Form 구조 정의
 * @ SF_ 로 시작 (Schema form)
 * @ SF_[Form 이름 메타 대문자, "_" 로 구분 ]
 * @ 검색폼은 _SRCH 로 구분
 */

// Schema of Bill Send Search Form !== 고지 전송 검색 폼 스키마 ==!
export const SF_BLL_SEND_SRCH: TFormSchema = {
    id: "form_bllSendSrch",
    schema: {
        cstmOfceCd: {
            type: "code",
            area: "cstmCd",
            label: "L_CSTM_OFCE",
            popupSize: "md",
            maxLength: 4,
            edit: false,
        },
        bllNo: {
            type: "text",
            label: "L_BLL_NO",
        },
        bllCstmOfceCd: {
            type: "text",
            maxLength: 4,
            size: 2,
            letterCase: "upper",
        },
        bllSysmCd: {
            type: "text",
            maxLength: 4,
            size: 2,
            letterCase: "upper",
        },
        bllYy: {
            type: "text",
            maxLength: 2,
            size: 1,
        },
        bllTpCd: {
            type: "text",
            maxLength: 1,
            size: 1,
            letterCase: "upper",
        },
        bllSrno: {
            type: "text",
            maxLength: 7,
            size: 2,
            letterCase: "upper",
        },
        bllDgr: {
            type: "text",
            maxLength: 1,
            size: 1,
        },
        sendDt: {
            type: "daterange",
            label: "L_SEND_DT",
            start: { name: "strtDt", required: true },
            end: { name: "endDt", required: true },
            required: true,
            rangeButton: 0,
            controlSize: 10,
        },
        sendStts: {
            type: "select",
            label: "L_SEND_STTS",
            area: "comnCd",
            comnCd: "OSC0075",
            excludes: ["0"],
            all: true,
            controlSize: 2,
        },
        tansadCstmOfceCd: {
            type: "text",
            maxLength: 4,
            size: 2,
            letterCase: "upper",
        },
        tansadYy: {
            type: "text",
            maxLength: 2,
            size: 1,
        },
        tansadSrno: {
            type: "text",
            maxLength: 7,
            size: 2,
        },
    },
};

// Schema of Bill Reception Search Form !== 고지 수신 검색 폼 스키마 ==!
export const SF_BLL_RCPN_SRCH: TFormSchema = {
    id: "form_bllRcpnSrch",
    schema: {
        cstmOfceCd: {
            type: "code",
            area: "cstmCd",
            label: "L_CSTM_OFCE",
            popupSize: "md",
            maxLength: 4,
            edit: false,
        },
        bllNo: {
            type: "text",
            label: "L_BLL_NO",
        },
        bllCstmOfceCd: {
            type: "text",
            maxLength: 4,
            size: 2,
            letterCase: "upper",
        },
        bllSysmCd: {
            type: "text",
            maxLength: 4,
            size: 2,
            letterCase: "upper",
        },
        bllYy: {
            type: "text",
            maxLength: 2,
            size: 1,
        },
        bllTpCd: {
            type: "text",
            maxLength: 1,
            size: 1,
            letterCase: "upper",
        },
        bllSrno: {
            type: "text",
            maxLength: 7,
            size: 3,
            letterCase: "upper",
        },
        bllDgr: {
            type: "text",
            maxLength: 1,
            size: 1,
        },
        rcpnDt: {
            type: "daterange",
            label: "L_RCPN_DT",
            start: { name: "strtDt", required: true },
            end: { name: "endDt", required: true },
            required: true,
            rangeButton: 0,
            controlSize: 10,
        },
        rcpnStts: {
            type: "select",
            label: "L_RCPN_STTS",
            area: "comnCd",
            comnCd: "OSC0075",
            excludes: ["0"],
            all: true,
            controlSize: 2,
        },
        tansadCstmOfceCd: {
            type: "text",
            maxLength: 4,
            size: 2,
            letterCase: "upper",
        },
        tansadYy: {
            type: "text",
            maxLength: 2,
            size: 1,
        },
        tansadSrno: {
            type: "text",
            maxLength: 7,
            size: 2,
        },
    },
};

// Schema of Security Reception Search Form !== 담보 수신 검색 폼 스키마 ==!
export const SF_SECU_RCPN_SRCH: TFormSchema = {
    id: "form_secuRcpnSrch",
    schema: {
        rcpnDt: {
            type: "daterange",
            label: "L_RCPN_DT",
            start: { name: "strtDt", required: true },
            end: { name: "endDt", required: true },
            required: true,
            rangeButton: 0,
            controlSize: 10,
        },
        coltDpstTpCd: {
            type: "select",
            label: "L_DPST_TP",
            options: [
                { label: "Taxpayer", value: "T" },
                { label: "CCE", value: "C" },
            ],
            all: true,
        },
        rcpnStts: {
            type: "select",
            label: "L_RCPN_STTS",
            area: "comnCd",
            comnCd: "OSC0075",
            excludes: ["0"],
            all: true,
        },
    },
};

// Schema of Bill Re-send Search Form !== 고지 재전송 검색 폼 스키마 ==!
export const SF_BLL_RESD_SRCH: TFormSchema = {
    id: "form_bllResdSrch",
    schema: {
        cstmOfceCd: {
            type: "code",
            area: "cstmCd",
            label: "L_CSTM_OFCE",
            popupSize: "md",
            maxLength: 4,
        },
        bllNo: {
            type: "text",
            label: "L_BLL_NO",
        },
        bllCstmOfceCd: {
            type: "text",
            maxLength: 4,
            size: 2,
            letterCase: "upper",
        },
        bllSysmCd: {
            type: "text",
            maxLength: 4,
            size: 2,
            letterCase: "upper",
        },
        bllYy: {
            type: "text",
            maxLength: 2,
            size: 1,
        },
        bllTpCd: {
            type: "text",
            maxLength: 1,
            size: 1,
            letterCase: "upper",
        },
        bllSrno: {
            type: "text",
            maxLength: 7,
            size: 3,
            letterCase: "upper",
        },
        bllDgr: {
            type: "text",
            maxLength: 1,
            size: 1,
        },
        sendDt: {
            type: "daterange",
            label: "L_SEND_DT",
            start: { name: "strtDt", required: true },
            end: { name: "endDt", required: true },
            rangeButton: 0,
            controlSize: 10,
            required: true,
        },
        sendStts: {
            type: "select",
            label: "L_SEND_STTS",
            area: "comnCd",
            comnCd: "OSC0075",
            excludes: ["0"],
            all: true,
        },
        tansadCstmOfceCd: {
            type: "text",
            maxLength: 4,
            size: 2,
            letterCase: "upper",
        },
        tansadYy: {
            type: "text",
            maxLength: 2,
            size: 1,
        },
        tansadSrno: {
            type: "text",
            maxLength: 7,
            size: 2,
        },
        sendTpCd: {
            type: "radio",
            label: "L_TP",
            options: [{ label: "ZanMalipo", value: "A" }],
            required: true,
        },
        ntcTpCd: {
            type: "radio",
            label: "L_NTC_TP",
            options: [
                { label: "Active", value: "A" },
                { label: "Extension", value: "D" },
                { label: "Cancel", value: "C" },
                { label: "Reuse", value: "R" },
            ],
            controlSize: 10,
            required: true,
        },
        ftcsSendTpCd: {
            type: "select",
            label: "L_FTCS_SEND_TP",
            area: "comnCd",
            comnCd: "OSC0076",
            excludes: ["L"],
            all: true,
        },
        sendReffNo: {
            type: "text",
            label: "L_REFF_NO",
        },
        ftcsSendNo: {
            type: "text",
            label: "L_FTCS_SEND_NO",
            mask: colUtils.getMaskPattern("8,7,2", "-"),
        },
    },
};

// Schema of Re-send Fail Search Form !== 재전송 실패 검색 폼 스키마 ==!
export const SF_RESD_FAIL_SRCH: TFormSchema = {
    id: "form_resdFailSrch",
    schema: {
        cstmOfceCd: {
            type: "code",
            area: "cstmCd",
            label: "L_CSTM_OFCE",
            popupSize: "md",
            maxLength: 4,
        },
        sendDt: {
            type: "daterange",
            label: "L_SEND_DT",
            start: { name: "strtDt", required: true },
            end: { name: "endDt", required: true },
            controlSize: 10,
            rangeButton: 0,
            required: true,
        },
        sendStts: {
            type: "select",
            label: "L_SEND_STTS",
            area: "comnCd",
            comnCd: "OSC0075",
            excludes: ["0"],
            edit: false,
            all: true,
        },
        sendTpCd: {
            type: "radio",
            label: "L_TP",
            options: [{ label: "ZanMalipo", value: "A" }],
            required: true,
        },
        ntcTpCd: {
            type: "select",
            label: "L_NTC_TP",
            options: [
                { label: "Active", value: "A" },
                { label: "Extension", value: "D" },
                { label: "Cancel", value: "C" },
                { label: "Reuse", value: "R" },
            ],
            all: true,
        },
    },
};
